import { MinimalUserContactSchema, UserContactOutSchema } from "@/api/client";
import { handleError } from "@/api/handleError";
import { getBusinessCallLogs } from "@/api/requests/callLogs";
import { getContacts, retrieveContact } from "@/api/requests/contacts";
import { getContactsFromLink } from "@/api/requests/misc";
import useAnalytics from "@/composables/useAnalytics";
import { useContactStore } from "@/stores/contact.store";
import { notify } from "@kyvg/vue3-notification";
import { ref } from "vue";
import { AMPLITUDE_EVENTS } from "./integrations/analytics/events";
import emitter from "./integrations/emitter";
import { getActiveBusinessId } from "./queries";
import {
  bulkDelete,
  createContact,
  deleteContact,
  editContact,
} from "./queries/contact";
import { TabFilterItemTypes, createContactType } from "./types/contact";

export const listContacts = async (link?: string) => {
  try {
    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    const response = link
      ? await getContactsFromLink({ link })
      : await getContacts({ businessId, ordering: "name", pageSize: 100 });

    return { contacts: response.data };
  } catch (error) {
    handleError({ error });
    throw error;
  }
};

export const getSingleContact = async (contact_id: string) => {
  try {
    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    const contactDetails = ref<UserContactOutSchema>();
    await retrieveContact({ businessId, contactId: contact_id }).then(
      (res) => (contactDetails.value = res.data)
    );
    return { contact: contactDetails.value };
  } catch (error) {
    handleError({ error });
  }
};

export const handleCreateContact = async (payload: createContactType) => {
  try {
    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    await createContact(payload, businessId);

    notify({
      text: "Contact created successfully",
      type: "success",
    });
  } catch (error) {
    handleError({ error });
  }
};

export const handleEditContacts = async (
  contactId?: string,
  payload?: UserContactOutSchema
) => {
  const analytics = useAnalytics();
  const contactStore = useContactStore();

  try {
    const businessId = getActiveBusinessId();

    if (!contactId || !payload) {
      return;
    }

    if (!businessId) {
      throw new Error("Missing business id");
    }

    const requestPayload = {
      ...payload,
      job_title: payload.job_title || null,
    };

    await editContact(contactId, requestPayload, businessId);
    void analytics.logEvent(AMPLITUDE_EVENTS.CONTACT_EDIT_SUCCESSFUL);

    contactStore.cachedSavedContactList =
      contactStore.cachedSavedContactList.map((contact) =>
        contact.id === contactId ? { ...contact, ...payload } : contact
      );

    notify({
      text: "Contact updated successfully",
      type: "success",
    });
    window.location.reload();
  } catch (error) {
    handleError({ error });
  }
};

export const handleSearchContacts = async (text: string) => {
  let contactsList: MinimalUserContactSchema[] = [];
  let nextLink: string | null = null;

  const businessId = getActiveBusinessId();

  try {
    if (!businessId) {
      throw new Error("Missing business id");
    }

    const contactsResponse = await getContacts({
      search: text,
      businessId,
    });

    if (contactsResponse?.data.data) {
      contactsList = contactsResponse.data.data;
      nextLink = contactsResponse.data.next;
    }
  } catch (error) {
    handleError({ error });
  }

  return { contacts: contactsList, next: nextLink };
};

export const filterContactList = async (
  filterType: TabFilterItemTypes,
  tagsFilter?: boolean
) => {
  const contactsList = ref<MinimalUserContactSchema[]>([]);
  const nextLink = ref<null | string>(null);

  try {
    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    if (tagsFilter) {
      const contactsResponse = await getContacts({
        businessId,
        tags: filterType,
        ordering: "-date_created",
        pageSize: 50,
      });

      contactsList.value = contactsResponse.data.data || [];
      nextLink.value = contactsResponse.data.next || null;
    } else if (filterType === "all") {
      const contactsResponse = await getContacts({ businessId });

      contactsList.value = contactsResponse.data.data || [];
    } else if (
      filterType === "recently_added" ||
      filterType === "recently_called"
    ) {
      const contactsResponse = await getContacts({
        businessId,
        ordering: "-date_created",
        pageSize: 50,
        tags: filterType,
      });

      contactsList.value = contactsResponse.data.data || [];
      nextLink.value = contactsResponse.data.next || null;
    } else if (filterType === "my_contacts") {
      const contactsResponse = await getContacts({
        businessId,
        ordering: "contact_name",
        pageSize: 50,
        tags: filterType,
      });

      contactsList.value = contactsResponse.data.data || [];
      nextLink.value = contactsResponse.data.next || null;
    }

    return { contacts: contactsList.value, next: nextLink.value };
  } catch (error) {
    handleError({ error });
    return { contacts: [], next: null };
  }
};

export const handleDeleteContact = async (id: string) => {
  try {
    const contactStore = useContactStore();

    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    await deleteContact(id, businessId);

    contactStore.cachedSavedContactList =
      contactStore.cachedSavedContactList.filter(
        (contact) => contact.id !== id
      );

    notify({
      text: "Contact deleted successfully",
      type: "success",
    });

    setTimeout(() => {
      window.location.reload();
    }, 1000);
  } catch (error) {
    handleError({ error });
  }
};

export const handleBulkDelete = async (
  contact_ids: Array<string>,
  deleteAll: boolean = false
): Promise<void> => {
  try {
    const contactStore = useContactStore();

    const businessId = getActiveBusinessId();

    if (!businessId) {
      throw new Error("Missing business id");
    }

    await bulkDelete(businessId, contact_ids, deleteAll);

    contactStore.cachedSavedContactList =
      contactStore.cachedSavedContactList.filter(
        (contact) => contact.id && !contact_ids.includes(contact.id)
      );

    notify({
      text: "Contacts deleted successfully",
      type: "success",
    });
    setTimeout(() => {
      // window.location.reload();
      emitter.emit("fetchContacts");
    }, 1000);
  } catch (error) {
    handleError({ error });
  }
};

export const contactLogs = async (phoneNumber: string) => {
  try {
    const res = await getBusinessCallLogs({
      phoneNumber,
      businessId: getActiveBusinessId() || 0,
      pageSize: 10,
    });
    return res;
  } catch (error) {
    handleError({ error });
  }
};
